import { useTranslation } from 'react-i18next';

import useAiChatToolLabel from '../../hooks/useAiChatToolLabel';
import useAiChatToolStatus from '../../hooks/useAiChatToolStatus';
import useOpenAgentSession from '../../hooks/useOpenAgentSession';
import readAgentSubTypeKey from '../../utils/readAgentSubTypeKey';
import AiChatToolCard from '../AiChatToolCard';

import type { ReactElement } from 'react';
import type { IAiChatToolCallOf } from '../../interfaces/IAiChatToolCall';

import styles from '../../sass/AiChat.module.scss';

/**
 * The `startAgentSearch` tool call: the AI search of an N-tier discovery session, started from
 * the chat in every tab or in some.
 *
 * The backend started the searches. When the part arrives live, `useAiChatToolActions` opens the
 * session modal on the map, where the tabs move with the socket. This row draws the part and
 * nothing more: how many tabs started, how many could not, and why behind the toggle. It shows no
 * tab status and polls nothing. From history it is the same row, and nothing opens.
 */
function StartAgentSearchToolCall({ part }: IAiChatToolCallOf<'startAgentSearch'>): ReactElement {
  const { t } = useTranslation();
  const label = useAiChatToolLabel('startAgentSearch');
  const status = useAiChatToolStatus(part.state);
  const openAgentSession = useOpenAgentSession();

  const output = part.state === 'output-available' ? part.output : undefined;

  const readSummary = (): string | undefined => {
    if (!output) {
      return status;
    }

    if (!output.ok) {
      return t('aiChat.tools.agentSessionRefused');
    }

    // An action the FE does not know opened the modal and nothing else; see `useAiChatToolActions`.
    if ((output.action as string) !== 'searchStarted') {
      return t('aiChat.tools.agentSessionOpened', { agent: t(readAgentSubTypeKey(output.subType)) });
    }

    return output.scope === 'all'
      ? t('aiChat.tools.agentSearchStartedAll')
      : t('aiChat.tools.agentSearchStartedTabs', {
        tabs: t('aiChat.tools.agentTabCount', { count: output.startedUnitIds.length }),
      });
  };

  // Under the header: which session while the call runs, the reason of a refusal, or how many
  // tabs could not start.
  const readNote = (): string | undefined => {
    if (!output) {
      return part.input?.sessionId
        ? t('aiChat.tools.agentSessionFallbackName', { id: part.input.sessionId })
        : undefined;
    }

    if (!output.ok) {
      return output.reason;
    }

    return output.failed?.length ? t('aiChat.tools.agentSearchFailed', { count: output.failed.length }) : undefined;
  };

  const handleOpen = () => {
    if (output?.ok) {
      openAgentSession({
        valueChainMapId: output.valueChainMapId, sessionId: output.sessionId, subType: output.subType,
      });
    }
  };

  // The reasons in the order the backend gave them, one per tab, never the tab's id.
  const details = output?.ok && output.failed?.length ? (
    <ul className={styles.toolWarnings}>
      {output.failed.map(({ unitId, reason }) => <li key={unitId}>{reason}</li>)}
    </ul>
  ) : undefined;

  const openButton = output?.ok ? (
    <button type="button" className={styles.toolToggle} onClick={handleOpen}>
      {t('aiChat.tools.agentOpenSession')}
    </button>
  ) : undefined;

  return (
    <AiChatToolCard
      name={label}
      status={readSummary()}
      content={openButton}
      details={details}
      errorText={part.errorText}
    >
      {readNote()}
    </AiChatToolCard>
  );
}

export default StartAgentSearchToolCall;
